import React, { useCallback, useMemo, useRef, useState } from "react";
import { useScrollIntoViewIfNotVisible } from "../../../hooks/useScrollIntoViewIfNotVisible";
import { yupResolver } from "@hookform/resolvers/yup";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { getErrorMessageFromApiError } from "@fmc-digital/assets";
import type { RecoverPassworRequestBodyBody } from "@fmc-digital/auth-api-generated";
import { useRecoverPasswordPost } from "@fmc-digital/auth-api-generated";
import { getCurrentLanguage, useTranslation } from "@fmc-digital/i18n";
import { RecoverPasswordLayout } from "@fmc-digital/ui";
import { ROUTES } from "@/constants/routes.constants";
import { config } from "@/config";
import { useRecoverUsername } from "@/contexts/RecoverUsernameContext";
import {
  clearRecoverPasswordHeaders,
  createRecoverPasswordPersonalInfoSchema,
  setRecoverPasswordHeaders,
} from "@/utils/recover-password.utils";
import { trackForgotPasswordEvent, ForgotPasswordEvent } from "@/utils/analytics.utils";
import { setAnalyticsEmailId } from "@/utils/analyticsUserContext";
import {
  createRecaptchaGenerationFailedError,
  createRecaptchaNotInitializedError,
} from "@/utils/recover-username.utils";
import type { FormValues } from "./recover-password-form.types";

/**
 * Auth-shell adapter for forgot-password personal info: validation, API, i18n → {@link RecoverPasswordLayout}.
 */
export const RecoverPasswordForm: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { setResponse, setEmail, setLastName } = useRecoverUsername();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { t } = useTranslation();

  const [responseBodyError, setResponseBodyError] = useState<unknown>(null);

  const {
    mutate: recoverPassword,
    isPending: isLoading,
    error,
  } = useRecoverPasswordPost();

  const validationSchema = useMemo(
    () =>
      createRecoverPasswordPersonalInfoSchema({
        validEmail: t("errors.validEmail"),
        validSsn: t("errors.validSsn"),
      }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: { email: "", ssn: "" },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const onSubmit = useCallback(
    async (values: FormValues) => {
      setAnalyticsEmailId(values.email.trim().toLowerCase());
      trackForgotPasswordEvent(ForgotPasswordEvent.CLICKED);
      setResponseBodyError(null);

      const requestBody: RecoverPassworRequestBodyBody = {
        username: values.email.trim().toLowerCase(),
        ssn: values.ssn.replace(/\D/g, ""),
      };

      if (!executeRecaptcha) {
        console.error("reCAPTCHA provider not initialized");
        setResponseBodyError(createRecaptchaNotInitializedError());
        return;
      }

      let captchaToken: string | undefined;
      try {
        captchaToken = await executeRecaptcha("recover_password");
        if (!captchaToken || captchaToken.trim() === "") {
          throw new Error("reCAPTCHA token generation returned empty");
        }
      } catch (err) {
        console.error("Failed to execute reCAPTCHA:", err);
        setResponseBodyError(createRecaptchaGenerationFailedError(err));
        return;
      }

      const guid = uuidv4();
      setRecoverPasswordHeaders(captchaToken, guid);

      recoverPassword(
        { data: requestBody },
        {
          onSuccess: (response) => {
            if (
              response &&
              "errorDetails" in response &&
              response.errorDetails
            ) {
              console.error("Recover password error in response body:", response);
              const errorObj = {
                data: { errorDetails: response.errorDetails },
                response: { data: { errorDetails: response.errorDetails } },
              };
              setResponseBodyError(errorObj);
              clearRecoverPasswordHeaders();
              return;
            }

            setResponseBodyError(null);
            console.log("Recover password response:", response);
            if (response) {
              trackForgotPasswordEvent(
                ForgotPasswordEvent.DATA_VALIDATION_SUCCESS,
              );
              setResponse(response as never);
              setEmail(values.email.trim().toLowerCase());
              const responseWithLastName = response as {
                lastName?: string;
                lname?: string;
              };
              const lastNameFromResponse =
                responseWithLastName.lastName ?? responseWithLastName.lname;

              if (
                lastNameFromResponse &&
                typeof lastNameFromResponse === "string" &&
                lastNameFromResponse.trim().length > 0
              ) {
                setLastName(lastNameFromResponse.trim());
              } else {
                console.warn(
                  "lastName not found in RecoverPassword API response. Password validation may fail.",
                );
              }
              if (response.guid) {
                config.setEnv("recoverPasswordGuid", response.guid);
              }
            }
            clearRecoverPasswordHeaders();
          },
          onError: (err) => {
            console.error("Recover password error:", err);
            setResponseBodyError(null);
            clearRecoverPasswordHeaders();
          },
        },
      );
    },
    [executeRecaptcha, recoverPassword, setResponse, setEmail, setLastName],
  );

  const handleCancel = useCallback(() => {
    form.reset();
    navigate(ROUTES.LOGIN);
  }, [form, navigate]);

  const getErrorMessage = useCallback((apiError: unknown): string => {
    try {
      const lang = getCurrentLanguage() ?? "en";
      return getErrorMessageFromApiError(apiError as never, lang);
    } catch {
      return getErrorMessageFromApiError(apiError as never, "en");
    }
  }, []);

  const watchedValues = form.watch();
  const hasRequiredValues =
    (watchedValues.email?.trim() ?? "").length > 0 &&
    (watchedValues.ssn?.replace(/\D/g, "") ?? "").length === 9;
  const isFormValid =
    form.formState.isValid && hasRequiredValues && !isLoading;

  const handleFormSubmit = useCallback(
    (e: React.SubmitEvent<HTMLFormElement>) => {
      e.preventDefault();
      form.handleSubmit(onSubmit)(e);
    },
    [form, onSubmit],
  );

  const showError = !!(error || responseBodyError);
  const errorBannerRef = useRef<HTMLDivElement>(null);
  useScrollIntoViewIfNotVisible(errorBannerRef, showError);

  const copy = useMemo(
    () => ({
      title: t("auth.forgotPassword.personalInfo.title"),
      description: t("auth.forgotPassword.personalInfo.description"),
      emailLabel: t("auth.forgotPassword.personalInfo.emailLabel"),
      emailPlaceholder: t("auth.forgotPassword.personalInfo.emailPlaceholder"),
      next: t("auth.forgotPassword.buttons.next"),
      cancel: t("auth.forgotPassword.buttons.cancel"),
    }),
    [t],
  );

  return (
    <RecoverPasswordLayout
      copy={copy}
      form={form}
      onFormSubmit={handleFormSubmit}
      onCancel={handleCancel}
      isLoading={isLoading}
      isSubmitDisabled={!isFormValid}
      error={showError ? getErrorMessage(error || responseBodyError) : null}
      errorBannerRef={errorBannerRef}
    />
  );
};
